import React, { useRef, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import './LossGraph.css';

const finiteNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

const normalizeMetric = (metric, index, trainingMode = 'sft') => {
  if (!metric) return null
  const step = finiteNumber(metric.step ?? metric.training_step ?? metric.trainingStep ?? metric.iteration ?? metric.epoch) ?? index + 1
  if (trainingMode === 'rl') {
    const reward = finiteNumber(
      metric.reward ??
      metric.score ??
      metric.value ??
      metric.final_reward ??
      metric.finalReward ??
      metric.mean_reward ??
      metric.meanReward
    )
    if (reward === undefined) {
      return null
    }
    return {
      step,
      reward
    }
  }
  const train_loss = finiteNumber(
    metric.train_loss ??
    metric.training_loss ??
    metric.trainingLoss ??
    metric.trainLoss ??
    metric.loss
  )
  const valid_loss = finiteNumber(
    metric.valid_loss ??
    metric.validation_loss ??
    metric.validationLoss ??
    metric.validLoss ??
    metric.eval_loss ??
    metric.evaluation_loss
  )
  if (train_loss === undefined && valid_loss === undefined) {
    return null
  }
  return {
    step,
    train_loss,
    valid_loss
  }
}

export const LossGraph = ({ metrics, model, dataset, jobId, trainingMode = 'sft' }) => {
  const chartRef = useRef(null);
  metrics = (metrics || [])
    .map((metric, index) => normalizeMetric(metric, index, trainingMode))
    .filter(x => x)
    .sort((a, b) => a.step - b.step)
  const isRL = trainingMode === 'rl'
  const title = isRL ? 'Reward Over Time' : 'Loss Over Time'
  const emptyLabel = isRL ? 'No reward metrics available yet.' : 'No loss metrics available yet.'

  if (metrics.length === 0) {
    return (
      <div className="lossGraphContainerStyle">
        {model}
        {dataset && <div className="lossGraphDataset">{dataset.name}</div>}
        <h2 className="lossGraphTitleStyle">{title}</h2>
        <div className="lossGraphEmpty">{emptyLabel}</div>
      </div>
    );
  }

  let max = 10000;
  if (metrics.length > max) {
    const stride = Math.floor(metrics.length / max);
    if (stride > 1) {
      metrics = metrics.filter((x, i) => i % stride === 0);
    }
  }
  const lossValues = isRL
        ? metrics.map(point => point.reward).filter(value => value !== undefined)
        : metrics.flatMap(point => [point.train_loss, point.valid_loss]).filter(value => value !== undefined)
  const sortedLosses = lossValues.slice().sort((a, b) => a - b);
  const upperBound = sortedLosses[Math.floor(0.75 * sortedLosses.length)];

  const datasets = []
  if (isRL && metrics.find(point => point.reward !== undefined)) {
    datasets.push({
      label: 'Reward',
      data: metrics.map((point) => point.reward),
      borderColor: '#22c55e',
      backgroundColor: 'rgba(34, 197, 94, 0.2)',
      borderWidth: 1.5,
      tension: 0.4,
      pointRadius: 0,
      spanGaps: true,
    })
  } else if (metrics.find(point => point.train_loss !== undefined)) {
    datasets.push({
      label: 'Training Loss',
      data: metrics.map((point) => point.train_loss),
      borderColor: '#2563eb',
      backgroundColor: 'rgba(37, 99, 235, 0.2)',
      borderWidth: 1.5,
      tension: 0.4,
      pointRadius: 0,
      spanGaps: true,
    })
  }
  if (metrics.find(point => point.valid_loss !== undefined)) {
    datasets.push({
      label: 'Validation Loss',
      data: metrics.map((point) => point.valid_loss),
      borderColor: '#f59e0b',
      backgroundColor: 'rgba(245, 158, 11, 0.2)',
      borderWidth: 1.5,
      tension: 0.4,
      pointRadius: 0,
      spanGaps: true,
    })
  }

  // Prepare data for Chart.js
  const chartData = {
    labels: metrics.map((point) => point.step),
    datasets,
  };

  // Chart.js options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = Number(context.raw)
            const formatted = Number.isFinite(value) ? value.toFixed(4) : '-'
            return `${context.dataset.label}: ${formatted}`
          },
          title: (context) => `Step: ${context[0].label}`,
        },
        backgroundColor: '#1a2b32',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
      },
      legend: {
        display: datasets.length > 1,
        labels: {
          color: '#fff',
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Training Step',
          color: '#fff',
        },
        ticks: {
          color: '#fff',
        },
        min: Math.min(...metrics.map((point) => point.step)), // Ensure full range
        max: Math.max(...metrics.map((point) => point.step)), 
      },
      y: {
        suggestedMax: upperBound,
        title: {
          display: true,
        text: isRL ? 'Reward' : 'Loss',
        color: '#fff',
      },
        ticks: {
          color: '#fff',
          callback: (value) => {
            return value.toFixed(2)
          },
        },
        beginAtZero: false, // Dynamically adjusts to data
      },
    },
  };

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.data = chartData; // Update data
      chartRef.current.options = chartOptions; // Update options
      chartRef.current.update(); // Force chart update
    }
  }, [jobId]);

  return (
    <div className="lossGraphContainerStyle">
      {model}
      {dataset && <div className="lossGraphDataset">{dataset.name}</div>}
      <h2 className="lossGraphTitleStyle">{title}</h2>
      <div className="lossGraphGraph">
        <Line ref={chartRef} key={jobId} data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};
